import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  onSuccess: () => void;
}

/**
 * Input del segundo factor (código TOTP de 6 dígitos o backup code de 10
 * chars). Se renderiza tras un login con password OK cuando el backend
 * indica requires_2fa: true.
 *
 * El usuario puede toggle entre "código" y "backup code". Validación de
 * formato es client-side; backend valida también.
 */
export function TotpVerifyInput({ onSuccess }: Props) {
  const { verify2FA } = useAuth();
  const [mode, setMode] = useState<"code" | "backup_code">("code");
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const trimmed = value.trim();
    if (mode === "code") {
      if (!/^\d{6}$/.test(trimmed)) {
        setError("Ingresa el código de 6 dígitos del autenticador.");
        return;
      }
    } else {
      if (trimmed.length < 6 || trimmed.length > 32) {
        setError("El código de respaldo es alfanumérico de hasta 32 caracteres.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await verify2FA(mode === "code" ? { code: trimmed } : { backup_code: trimmed });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Código inválido.");
      } else if (err instanceof ApiError && err.status === 423) {
        setError("Cuenta bloqueada por intentos fallidos. Prueba más tarde.");
      } else {
        setError("No se pudo completar la verificación.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <div className="space-y-2">
        <Label htmlFor="totp-code">
          {mode === "code" ? "Código del autenticador" : "Código de respaldo"}
        </Label>
        <Input
          id="totp-code"
          autoFocus
          inputMode={mode === "code" ? "numeric" : "text"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={submitting}
          placeholder={mode === "code" ? "123456" : "abcdef1234"}
          maxLength={mode === "code" ? 6 : 32}
        />
      </div>
      {error && (
        <div
          role="alert"
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? "Verificando…" : "Verificar"}
      </Button>
      <button
        type="button"
        className="text-xs text-muted-foreground underline hover:text-foreground"
        onClick={() => {
          setMode(mode === "code" ? "backup_code" : "code");
          setValue("");
          setError(null);
        }}
      >
        {mode === "code" ? "Usar código de respaldo" : "Volver al código del autenticador"}
      </button>
    </form>
  );
}
